import { FC, useEffect, useState } from 'react';
import { Alert, Box, Drawer, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CustomReactDatePicker from '../../../../../common/CustomReactDatePicker';
import CustomTimePicker from '../../../../../common/CustomTimePicker';
import CustomButton from '../../../../../common/CustomButton';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import {
  combineDateAndTime,
  getMinEndTimeForSameDay,
  validateEndDateTimeAfterStart,
} from '../../../../../utils/dateUtils';

import styles from './TripStopScheduleModal.module.scss';

export type TripStopScheduleFormValues = {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
};

export type TripStopScheduleInitial = Partial<TripStopScheduleFormValues>;

export interface TripStopScheduleResult {
  tripStopStartDateTime: string;
  tripStopEndDateTime: string;
}

export interface TripStopScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (result: TripStopScheduleResult) => void;
  initialValues?: TripStopScheduleInitial | null;
  titleId?: string;
  minDate?: string;
  maxDate?: string;
  errorText?: string | null;
  onErrorDismiss?: () => void;
  isSubmitting?: boolean;
}

const EMPTY_VALUES: TripStopScheduleFormValues = {
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
};

const TripStopScheduleModal: FC<TripStopScheduleModalProps> = ({
  open,
  onClose,
  onCreate,
  initialValues = null,
  titleId = 'trip-stop-schedule-modal-title',
  minDate,
  maxDate,
  errorText,
  onErrorDismiss,
  isSubmitting = false,
}) => {
  const isMobile = useIsMobile();
  const [values, setValues] = useState<TripStopScheduleFormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Partial<Record<keyof TripStopScheduleFormValues, string>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!open) {
      setValues(EMPTY_VALUES);
      setErrors({});
      setSubmitAttempted(false);
      return;
    }

    setValues({
      startDate: initialValues?.startDate ?? '',
      startTime: initialValues?.startTime ?? '',
      endDate: initialValues?.endDate ?? '',
      endTime: initialValues?.endTime ?? '',
    });
    setErrors({});
    setSubmitAttempted(false);
  }, [open, initialValues]);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
    if (submitAttempted) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name as keyof TripStopScheduleFormValues];
        return next;
      });
    }
  };

  const validate = (): Partial<Record<keyof TripStopScheduleFormValues, string>> => {
    const next: Partial<Record<keyof TripStopScheduleFormValues, string>> = {};

    if (!values.startDate.trim()) next.startDate = 'Start date is required';
    if (!values.startTime.trim()) next.startTime = 'Start time is required';
    if (!values.endDate.trim()) next.endDate = 'End date is required';
    if (!values.endTime.trim()) next.endTime = 'End time is required';

    const rangeErrors = validateEndDateTimeAfterStart(
      values.startDate,
      values.startTime,
      values.endDate,
      values.endTime
    );
    if (rangeErrors.endDate) next.endDate = rangeErrors.endDate;
    if (rangeErrors.endTime) next.endTime = rangeErrors.endTime;

    const tripMin = minDate?.trim().slice(0, 10);
    const tripMax = maxDate?.trim().slice(0, 10);
    if (tripMin && values.startDate.trim() && values.startDate < tripMin) {
      next.startDate = `Start date must be on or after ${tripMin}`;
    }
    if (tripMax && values.endDate.trim() && values.endDate > tripMax) {
      next.endDate = `End date must be on or before ${tripMax}`;
    }

    return next;
  };

  const handleCreate = () => {
    setSubmitAttempted(true);
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const tripStopStartDateTime = combineDateAndTime(values.startDate, values.startTime);
    const tripStopEndDateTime = combineDateAndTime(values.endDate, values.endTime);

    if (!tripStopStartDateTime || !tripStopEndDateTime) {
      return;
    }

    onCreate({ tripStopStartDateTime, tripStopEndDateTime });
  };

  const backdropSx = { backgroundColor: 'rgba(34, 43, 58, 0.45)' };

  const dialogContent = (
    <>
      <Box className={styles.header}>
        <Typography
          id={titleId}
          variant="h5Bold"
          className={styles.title}
        >
          Stop start & end
        </Typography>
        <IconButton
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography
        variant="h11"
        className={styles.subtitle}
      >
        Trip stops need a start and end date and time.
      </Typography>

      <Box className={styles.body}>
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            name="startDate"
            inputLabel="Start Date"
            placeholder="Select start date"
            value={values.startDate}
            onChange={handleFieldChange}
            error={submitAttempted && !!errors.startDate}
            helperText={submitAttempted ? errors.startDate : ''}
            min={minDate}
            max={maxDate}
          />
          <CustomTimePicker
            name="startTime"
            inputLabel="Start Time"
            placeholder="Select start time"
            value={values.startTime}
            onChange={handleFieldChange}
            error={submitAttempted && !!errors.startTime}
            helperText={submitAttempted ? errors.startTime : ''}
          />
        </Box>

        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            name="endDate"
            inputLabel="End Date"
            placeholder="Select end date"
            value={values.endDate}
            onChange={handleFieldChange}
            error={submitAttempted && !!errors.endDate}
            helperText={submitAttempted ? errors.endDate : ''}
            min={values.startDate || minDate || undefined}
            max={maxDate}
          />
          <CustomTimePicker
            name="endTime"
            inputLabel="End Time"
            placeholder="Select end time"
            value={values.endTime}
            onChange={handleFieldChange}
            minTime={getMinEndTimeForSameDay(values.startDate, values.endDate, values.startTime)}
            error={submitAttempted && !!errors.endTime}
            helperText={submitAttempted ? errors.endTime : ''}
          />
        </Box>

        {errorText ? (
          <Alert
            severity="error"
            onClose={onErrorDismiss}
            sx={{ mt: 1, flexShrink: 0 }}
          >
            {errorText}
          </Alert>
        ) : null}
      </Box>

      <Box className={styles.footer}>
        <CustomButton
          variant="outlined"
          btnType="button"
          onClick={onClose}
          customClass={styles.footerBtn}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="contained"
          btnType="button"
          onClick={handleCreate}
          customClass={styles.footerBtn}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving…' : 'Create'}
        </CustomButton>
      </Box>
    </>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          aria-labelledby={titleId}
          slotProps={{
            backdrop: { sx: backdropSx },
          }}
          PaperProps={{
            className: `${styles.drawerPaper} ${styles.scheduleModalSurface}`,
            elevation: 8,
            sx: { zIndex: 1400, overflow: 'visible' },
          }}
          sx={{ zIndex: 1400 }}
        >
          {dialogContent}
        </Drawer>
      ) : (
        <Modal
          open={open}
          onClose={onClose}
          aria-labelledby={titleId}
          sx={{ zIndex: 1400 }}
          slotProps={{
            backdrop: { sx: backdropSx },
          }}
        >
          <Box className={`${styles.modalPaper} ${styles.scheduleModalSurface}`}>{dialogContent}</Box>
        </Modal>
      )}
    </>
  );
};

export default TripStopScheduleModal;
